import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { throwError } from 'rxjs/internal/observable/throwError';
import { catchError, map } from 'rxjs/operators';
import { ErrorService } from '../error';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {

  /**
   * Every language DeepL accepts as a translation source. This is a capability
   * list, not a permission list: what a contributor may submit is still gated by
   * their admin_approved language skills, which this is intersected with.
   *
   * Must stay in step with the backend's config/translation.deepl.source_locales.
   * Regenerate both from the provider rather than by hand:
   *   GET https://api.deepl.com/v3/languages?resource=translate_text
   * taking every entry with usable_as_source: true.
   *
   * The three-letter codes below are inert for now: these are matched against
   * Language.iso, which is iso_639_1, so only the two-letter ones can ever match.
   * They are kept so the list stays a faithful copy of what DeepL supports.
   */
  private _availableTranslationSourceLanguageCodes : string[] = [
    'ace',   // Acehnese
    'af',    // Afrikaans
    'an',    // Aragonese
    'ar',    // Arabic
    'as',    // Assamese
    'ay',    // Aymara
    'az',    // Azerbaijani
    'ba',    // Bashkir
    'be',    // Belarusian
    'bg',    // Bulgarian
    'bho',   // Bhojpuri
    'bn',    // Bengali
    'br',    // Breton
    'bs',    // Bosnian
    'ca',    // Catalan
    'ceb',   // Cebuano
    'ckb',   // Kurdish (Sorani)
    'cs',    // Czech
    'cy',    // Welsh
    'da',    // Danish
    'de',    // German
    'el',    // Greek
    'en',    // English
    'eo',    // Esperanto
    'es',    // Spanish
    'et',    // Estonian
    'eu',    // Basque
    'fa',    // Persian
    'fi',    // Finnish
    'fr',    // French
    'ga',    // Irish
    'gl',    // Galician
    'gn',    // Guarani
    'gom',   // Konkani
    'gu',    // Gujarati
    'ha',    // Hausa
    'he',    // Hebrew
    'hi',    // Hindi
    'hr',    // Croatian
    'ht',    // Haitian Creole
    'hu',    // Hungarian
    'hy',    // Armenian
    'id',    // Indonesian
    'ig',    // Igbo
    'is',    // Icelandic
    'it',    // Italian
    'ja',    // Japanese
    'jv',    // Javanese
    'ka',    // Georgian
    'kk',    // Kazakh
    'kmr',   // Kurdish (Kurmanji)
    'ko',    // Korean
    'ky',    // Kyrgyz
    'la',    // Latin
    'lb',    // Luxembourgish
    'lmo',   // Lombard
    'ln',    // Lingala
    'lt',    // Lithuanian
    'lv',    // Latvian
    'mai',   // Maithili
    'mg',    // Malagasy
    'mi',    // Maori
    'mk',    // Macedonian
    'ml',    // Malayalam
    'mn',    // Mongolian
    'mr',    // Marathi
    'ms',    // Malay
    'mt',    // Maltese
    'my',    // Burmese
    'nb',    // Norwegian (bokmål)
    'ne',    // Nepali
    'nl',    // Dutch
    'oc',    // Occitan
    'om',    // Oromo
    'pa',    // Punjabi
    'pag',   // Pangasinan
    'pam',   // Kapampangan
    'pl',    // Polish
    'prs',   // Dari
    'ps',    // Pashto
    'pt',    // Portuguese
    'qu',    // Quechua
    'ro',    // Romanian
    'ru',    // Russian
    'sa',    // Sanskrit
    'scn',   // Sicilian
    'sk',    // Slovak
    'sl',    // Slovenian
    'sq',    // Albanian
    'sr',    // Serbian
    'st',    // Sesotho
    'su',    // Sundanese
    'sv',    // Swedish
    'sw',    // Swahili
    'ta',    // Tamil
    'te',    // Telugu
    'tg',    // Tajik
    'th',    // Thai
    'tk',    // Turkmen
    'tl',    // Tagalog
    'tn',    // Tswana
    'tr',    // Turkish
    'ts',    // Tsonga
    'tt',    // Tatar
    'uk',    // Ukrainian
    'ur',    // Urdu
    'uz',    // Uzbek
    'vi',    // Vietnamese
    'wo',    // Wolof
    'xh',    // Xhosa
    'yi',    // Yiddish
    'yue',   // Cantonese
    'zh',    // Chinese
    'zu',    // Zulu
  ];

  constructor(
    private http: HttpClient,
    private errorService: ErrorService,

  ) {

  }

  get availableTranslationSourceLanguageCodes() {
    return this._availableTranslationSourceLanguageCodes;
  }

  getDefaultLocaleFromGenericLanguage(languageCode: string): string {
    const languageMap: { [key: string]: string } = {
      'en': 'en-GB',
      'pt': 'pt-BR', // Brazilian by default!
      'zh': 'zh-HANS' // Simplified Chinese (HANT for Traditional)
    };
    return languageMap[languageCode] || languageCode;
  }

  getTranslation(textsAsObject:{[key:string]:string},target_lang : string, source_lang : string) {

    const keys = Object.keys(textsAsObject);
    const values = Object.values(textsAsObject);

    const params = {
      text: values,
      target_lang: target_lang,
      source_lang: source_lang
    };

    return this.http.post<{translated_text: string[]}>('api/v1/translate', params)
      .pipe(
        map((response) => {

            const translatedTexts = response.translated_text;
            const translatedTextsAsObject = {};
            if(keys.length !== 1){
              keys.forEach((key,index) => {
                translatedTextsAsObject[key] = translatedTexts[index];
              });
            } else {
              translatedTextsAsObject[keys[0]] = translatedTexts;
            }

            return translatedTextsAsObject;
        }),
        catchError(error => this.handleError(error))
    );
  }
  private handleError(errorResponse: HttpErrorResponse) {
    let errorMessage = 'error.something_went_wrong';
    if (!errorResponse.error || !errorResponse.error.message) {
      return throwError(errorMessage);
    }
    // if (errorResponse.error.errors?.slug?.[0] === 'The slug has already been taken.'){
    //   errorMessage ="content_management.slug_availability_error";
    //   return throwError(errorMessage);
    // }
    const message = errorResponse.error.message;
    const standardErrorMessageTranslationKey = this.errorService.getCommonErrorMessageTranslationKey(message);
    if(standardErrorMessageTranslationKey){
      errorMessage = standardErrorMessageTranslationKey;
    }
    if (errorResponse.error.meta){
      return throwError({message:errorMessage,meta:errorResponse.error.meta});
    }
    return throwError(errorMessage);
  }
  setPreferredTranslationSourceLanguage(iso: string) {
    localStorage.setItem('preferredTranslationSourceLanguage', iso);
  }
  getPreferredTranslationSourceLanguage() : string | null {
    return localStorage.getItem('preferredTranslationSourceLanguage');
  }
}
